import { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  X,
  Building2,
  Users,
  Search,
  ExternalLink,
  AlertTriangle
} from 'lucide-react';
import {
  dashboardService,
  type OmsDrillBucket,
  type OmsDrillDownResponse,
  type OmsResourceRow,
  type DrillDownProject,
  type ManagerHealthLevel
} from '../../../services/dashboardService';

interface Props {
  bucket: OmsDrillBucket | null;
  onClose: () => void;
  onOpenProject?: (projectId: string) => void;
}

const HEALTH_DOT: Record<ManagerHealthLevel, string> = {
  good: 'bg-emerald-500',
  attention: 'bg-amber-500',
  critical: 'bg-rose-500',
  notStarted: 'bg-slate-400'
};
const HEALTH_LABEL: Record<ManagerHealthLevel, string> = {
  good: 'good',
  attention: 'attention',
  critical: 'critical',
  notStarted: 'not started'
};
const HEALTH_BADGE: Record<ManagerHealthLevel, string> = {
  good: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
  attention: 'bg-amber-500/10 text-amber-700 dark:text-amber-300',
  critical: 'bg-rose-500/10 text-rose-700 dark:text-rose-300',
  notStarted: 'bg-slate-400/15 text-slate-600 dark:text-slate-300'
};

function ProjectRow({
  project,
  onOpenProject
}: {
  project: DrillDownProject;
  onOpenProject?: (id: string) => void;
}) {
  const completionPct = project.totalTasks > 0
    ? Math.round((project.completedTasks / project.totalTasks) * 100)
    : 0;
  return (
    <li className="rounded-xl border border-slate-100 bg-white p-3 shadow-sm transition hover:border-cyan-200 hover:shadow-md dark:border-slate-700/40 dark:bg-slate-900/40 dark:hover:border-slate-600">
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${HEALTH_DOT[project.health]}`} />
          <div className="min-w-0">
            <p className="truncate text-sm font-bold text-slate-900 dark:text-white">{project.projectName}</p>
            <p className="truncate text-xs text-slate-500 dark:text-slate-400">
              {project.code} · {project.customer} · {project.department}
            </p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <div className="hidden text-right sm:block">
            <p className="text-xs font-semibold text-slate-700 dark:text-slate-300">{project.pendingTasks} pending</p>
            <p className="text-[10px] text-slate-500">{completionPct}% done</p>
          </div>
          <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${HEALTH_BADGE[project.health]}`}>
            {HEALTH_LABEL[project.health]}
          </span>
          {onOpenProject && (
            <button
              type="button"
              onClick={() => onOpenProject(project.projectId)}
              className="flex items-center gap-1 rounded-lg bg-cyan-500/10 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-cyan-700 hover:bg-cyan-500/20 dark:text-cyan-300"
            >
              Open <ExternalLink className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>
      <div className="mt-2 grid grid-cols-2 gap-2 text-[11px] sm:grid-cols-4">
        <Stat label="Total" value={project.totalTasks} />
        <Stat label="Pass" value={project.completedTasks} />
        <Stat label="Fail" value={project.failedTasks} />
        <Stat label="Pending" value={project.pendingTasks} />
      </div>
    </li>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg bg-slate-50 px-2 py-1 dark:bg-slate-800/50">
      <p className="text-[9px] font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      <p className="text-xs font-bold text-slate-800 dark:text-slate-200">{value.toLocaleString()}</p>
    </div>
  );
}

function ResourceRow({ resource }: { resource: OmsResourceRow }) {
  const lastSubmit = resource.lastSubmittedAt ? new Date(resource.lastSubmittedAt) : null;
  const lastLabel = lastSubmit
    ? lastSubmit.toLocaleString(undefined, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
    : 'Never';
  const statusLabel = resource.isWorking
    ? 'working'
    : resource.hasEverSubmitted
      ? 'idle'
      : 'never submitted';
  const statusClass = resource.isWorking
    ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
    : resource.hasEverSubmitted
      ? 'bg-amber-500/10 text-amber-700 dark:text-amber-300'
      : 'bg-rose-500/10 text-rose-700 dark:text-rose-300';
  return (
    <li className="rounded-xl border border-slate-100 bg-white p-3 shadow-sm dark:border-slate-700/40 dark:bg-slate-900/40">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-bold text-slate-900 dark:text-white">{resource.resourceName}</p>
          <p className="truncate text-xs text-slate-500 dark:text-slate-400">
            {resource.roleName || 'Unknown role'} · last submit {lastLabel}
          </p>
        </div>
        <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${statusClass}`}>
          {statusLabel}
        </span>
      </div>
      <div className="mt-2 grid grid-cols-3 gap-2 text-[11px]">
        <Stat label="Completed" value={resource.totalCompleted} />
        <Stat label="Pending" value={resource.totalPending} />
        <Stat label="Projects" value={resource.projects.length} />
      </div>
      {resource.projects.length > 0 && (
        <ul className="mt-2 space-y-1">
          {resource.projects.slice(0, 5).map(p => (
            <li
              key={p.projectId}
              className="flex items-center justify-between gap-2 rounded-md bg-slate-50 px-2 py-1 text-[11px] dark:bg-slate-800/50"
            >
              <span className="truncate text-slate-700 dark:text-slate-200">{p.projectName}</span>
              <span className="shrink-0 text-slate-500">
                <span className="text-emerald-600 dark:text-emerald-400">✓ {p.passTasks}</span>
                {' / '}
                <span className="text-rose-600 dark:text-rose-400">✗ {p.failTasks}</span>
              </span>
            </li>
          ))}
          {resource.projects.length > 5 && (
            <li className="text-center text-[10px] text-slate-500">
              + {resource.projects.length - 5} more project(s)
            </li>
          )}
        </ul>
      )}
    </li>
  );
}

export default function OmsDrillDownPanel({ bucket, onClose, onOpenProject }: Props) {
  const [data, setData] = useState<OmsDrillDownResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!bucket) {
      setData(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setSearch('');
    dashboardService
      .getOmsDrillDown(bucket)
      .then(res => {
        if (!cancelled) setData(res);
      })
      .catch(err => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load drill-down');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [bucket]);

  const isResourceBucket = bucket === 'omsResourceTotal'
    || bucket === 'omsResourceWorking'
    || bucket === 'omsResourceInactive';

  const filteredProjects = useMemo(() => {
    if (!data?.projects) return [];
    const q = search.trim().toLowerCase();
    if (!q) return data.projects;
    return data.projects.filter(p =>
      p.projectName.toLowerCase().includes(q)
      || (p.code || '').toLowerCase().includes(q)
      || (p.customer || '').toLowerCase().includes(q)
    );
  }, [data, search]);

  const filteredResources = useMemo(() => {
    if (!data?.resources) return [];
    const q = search.trim().toLowerCase();
    if (!q) return data.resources;
    return data.resources.filter(r =>
      r.resourceName.toLowerCase().includes(q)
      || (r.roleName || '').toLowerCase().includes(q)
    );
  }, [data, search]);

  return (
    <AnimatePresence>
      {bucket && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-40 bg-slate-900/40 backdrop-blur-sm"
          />
          <motion.aside
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 28, stiffness: 280 }}
            className="fixed inset-y-0 right-0 z-50 flex w-full max-w-2xl flex-col bg-white shadow-2xl dark:bg-slate-900"
          >
            <header className="flex items-start justify-between gap-3 border-b border-slate-100 px-5 py-4 dark:border-slate-700/50">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-cyan-500/10 text-cyan-600 dark:text-cyan-400">
                  {isResourceBucket ? <Users className="h-5 w-5" /> : <Building2 className="h-5 w-5" />}
                </div>
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400">
                    OMS Drill-down
                  </p>
                  <h2 className="text-lg font-bold text-slate-900 dark:text-white">
                    {data?.bucketLabel ?? 'Loading…'}
                  </h2>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {data ? `${data.total} item${data.total === 1 ? '' : 's'}` : ''}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-white"
              >
                <X className="h-5 w-5" />
              </button>
            </header>

            <div className="border-b border-slate-100 px-5 py-3 dark:border-slate-700/50">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder={isResourceBucket ? 'Search resource…' : 'Search project, code, customer…'}
                  className="w-full rounded-xl border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm text-slate-700 placeholder:text-slate-400 focus:border-cyan-400 focus:outline-none focus:ring-1 focus:ring-cyan-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-5">
              {error ? (
                <div className="flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm dark:border-rose-900/40 dark:bg-rose-900/20">
                  <AlertTriangle className="h-4 w-4 shrink-0 text-rose-600" />
                  <p className="text-rose-700 dark:text-rose-300">{error}</p>
                </div>
              ) : loading ? (
                <div className="space-y-2">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="h-16 animate-pulse rounded-xl bg-slate-100 dark:bg-slate-800/40" />
                  ))}
                </div>
              ) : isResourceBucket ? (
                filteredResources.length === 0 ? (
                  <p className="py-10 text-center text-sm text-slate-500">No resources match.</p>
                ) : (
                  <ul className="space-y-2">
                    {filteredResources.map(r => <ResourceRow key={r.resourceId} resource={r} />)}
                  </ul>
                )
              ) : filteredProjects.length === 0 ? (
                <p className="py-10 text-center text-sm text-slate-500">No projects match.</p>
              ) : (
                <ul className="space-y-2">
                  {filteredProjects.map(p => (
                    <ProjectRow key={p.projectId} project={p} onOpenProject={onOpenProject} />
                  ))}
                </ul>
              )}
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
